/**
 * THE LIGHT LAYER (B3) — routes and the lit site outline as light (spec §D.4, §D.8, §E).
 *
 *   createLightLayer(map, { beforeId: ANCHORS.routes, reducedMotion })
 *
 * One MapLibre CustomLayerInterface draws every path (all routes + the site
 * outline) from ONE static instance buffer. Per-path visual state lives in an
 * RGBA32F texture (state.ts) re-uploaded with one texSubImage2D per frame when
 * something changed; setState() only edits a JS object and asks for a frame.
 *
 * Stack per frame (renderer.ts): glow (quarter-res, MAX, composited with screen
 * × --ld-glow-gain) → shade (--ld-route-shade, core + --ld-route-shade-px) →
 * core (trail mix while drawing, light packet) → comet sprites.
 *
 * Owned here, derived from setState (internals.ts): trail settle (640 settle on
 * arrival), comet fade in (160) / out (420 settle, scale 1 → 0.6), palette drift
 * on a mood change (1100 drift; 160 under reduced motion, spec §E.10). A lit comet sits
 * on `head`, or on `pulse` when pulse ≥ 0 (the drive: route drawn, comet at s).
 *
 * Layer flags: kind "route" paths follow appStore.layers.routes, kind "site"
 * follows layers.boundary (multiplies alpha; state is kept).
 *
 * Modes: "gl" (default) or "fallback" (MapLibre line layers, fallback.ts) when a
 * shader/framebuffer fails, or with ?light=fallback. ?light=fail simulates a
 * shader failure at onAdd (tests the live switch); a framebuffer or GL error inside a
 * render hook also switches (it never escapes into MapLibre's frame). ?light=gl disables
 * the frame governor (keeps the glow on slow software rasterisers). WebGL context loss:
 * resources are rebuilt from the stored paths when the style reloads.
 */
import type { CustomLayerInterface, CustomRenderMethodInput, Map as MLMap } from "maplibre-gl";
import { LAYER_IDS, type CreateLightLayer, type LightLayerAPI, type LightPalette, type LightPath, type RouteVisualState } from "@/contracts/map";
import type { AppState, Layout, Mood } from "@/contracts/store";
import { EASE } from "@/design";
import type { LngLat } from "@/lib/geo";
import { ticker } from "@/lib/ticker";
import { MOODS, MOOD_TRANSITION_MS } from "@/map/style/moods";
import { appStore } from "@/store/store";
import { TRAIL_PX, mixMoodLight, mixPalette, moodLightFrom, toGLPalette, type GLPalette, type MoodLight } from "./color";
import { LightFallback, type FallbackFrame } from "./fallback";
import { FrameGovernor, onStateChange, restAnim, settleAnim, stepAnim, type PathAnim } from "./internals";
import { effectivePixelRatio, relativeMatrix } from "./matrix";
import { instanceRanges, packPaths, pointAtPacked, relMercator, toDrawnM, type PackedPath, type PackedPaths } from "./pack";
import { COMET_FLOATS, LightRenderer, type FrameParams } from "./renderer";
import { GLOW_SIGMA_MIN_PX, GLOW_SIGMA_PER_WIDTH } from "./shaders";
import {
  COMET_CORE_PX,
  COMET_DOT_PX,
  COMET_HALO_PX,
  STATE_FLOATS,
  STYLE_COMET,
  applyPatch,
  defaultState,
  isArrived,
  relayout,
  styleForKind,
  writeStateRow,
  type StateEntry,
} from "./state";

export { packPaths, pointAtPacked } from "./pack";
export type { PackedPaths } from "./pack";

export interface LightLayerOptions {
  beforeId: string;
  reducedMotion: () => boolean;
  /** "auto" (default) = gl unless ?light=fallback; "fallback" forces MapLibre line layers. */
  mode?: "auto" | "gl" | "fallback";
  /** Frame governor (drop the glow when camera-motion frames run at a median > 28 ms; internals.ts). Default on unless ?light=gl. */
  governor?: boolean;
}

/** Diagnostics for harnesses and QA (not part of the contract). */
export interface LightDebugInfo {
  mode: "gl" | "fallback";
  paths: number;
  instances: number;
  visible: number;
  comets: number;
  glow: boolean;
  glowDropped: boolean;
  frameMsAvg: number; //  MapLibre frame interval (ms), last ≤ 30 consecutive frames
  cpuMsAvg: number; //    layer CPU time per frame (prerender + render), last ≤ 60 frames
  lastError: string | null;
}

export type LightLayerHandle = LightLayerAPI & { debug(): LightDebugInfo };

const EARTH_M = 40075016.686;
/** Mood drift under reduced motion (spec §E.10: "Mood 160"). */
const REDUCED_MOOD_MS = 160;

const urlLight = (): string | null => {
  try {
    return typeof location !== "undefined" ? new URLSearchParams(location.search).get("light") : null;
  } catch {
    return null;
  }
};

const copyState = (s: RouteVisualState): RouteVisualState => ({ ...s });

export function createLightLayer(map: MLMap, o: LightLayerOptions): LightLayerHandle {
  const url = urlLight();
  const forceFallback = o.mode === "fallback" || url === "fallback";
  const simulateFail = url === "fail";
  const governorOn = o.governor ?? url !== "gl";
  const reduced = (): boolean => {
    try {
      return !!o.reducedMotion();
    } catch {
      return false;
    }
  };

  let mode: "gl" | "fallback" = forceFallback ? "fallback" : "gl";
  let destroyed = false;
  let lastError: string | null = null;
  const modeListeners = new Set<(m: "gl" | "fallback") => void>();

  /* ── paths + state ─────────────────────────────────────────────────── */
  let packed: PackedPaths | null = null;
  const entries = new Map<string, StateEntry>();
  const kinds = new Map<string, LightPath["kind"]>();
  const anims = new Map<string, PathAnim>();
  const touched = new Map<string, number>();
  const styled = new Set<string>(); // ids whose style was set explicitly (setPaths never overrides those)
  let touchSeq = 0;
  let stateBuf = new Float32Array(STATE_FLOATS);
  let stateDirty = true; //   rows need rewriting
  let uploadNeeded = true; // rows rewritten but not yet on the GPU
  let visible: PackedPath[] = [];
  let glowing: PackedPath[] = [];
  const lineRanges: number[] = []; // instance ranges of the lit paths (pack.ts instanceRanges)
  const glowRanges: number[] = []; // … of the lit paths that glow
  let anyGlow = false;
  let cometBuf = new Float32Array(8 * COMET_FLOATS);
  let cometCount = 0;
  let cometUpload = false;

  const app0 = appStore.getState();
  let layout: Layout = app0.layout;
  let layerFlags = { routes: app0.layers.routes, boundary: app0.layers.boundary };

  /* ── palette + mood light ──────────────────────────────────────────── */
  let palette: GLPalette = toGLPalette(MOODS[app0.mood].light);
  let light: MoodLight = moodLightFrom(MOODS[app0.mood].cssVars);
  let palTween: { from: GLPalette; to: GLPalette; t0: number; dur: number } | null = null;
  let lightTween: { from: MoodLight; to: MoodLight; t0: number; dur: number } | null = null;
  let paletteOff: (() => void) | null = null;

  /* ── GL ────────────────────────────────────────────────────────────── */
  let renderer: LightRenderer | null = null;
  let fallback: LightFallback | null = null;
  let frame: FrameParams | null = null;
  let prepared = false;
  const gov = new FrameGovernor(governorOn);
  const cpuSamples: number[] = [];
  let failed = false;
  const matF32 = new Float32Array(16);
  const matF64 = new Float64Array(16);
  const colors = new Float32Array(15);

  const entryFor = (id: string): StateEntry => {
    let e = entries.get(id);
    if (!e) {
      e = { s: defaultState(kinds.get(id) ?? "route", layout), widthSet: false, glowSet: false };
      entries.set(id, e);
    }
    return e;
  };

  const visibilityOf = (p: PackedPath): number =>
    p.kind === "route" ? (layerFlags.routes ? 1 : 0) : p.kind === "site" ? (layerFlags.boundary ? 1 : 0) : 1;

  const requestRender = (): void => {
    if (destroyed) return;
    if (mode === "fallback") scheduleFallback();
    else {
      try {
        map.triggerRepaint();
      } catch {
        /* map removed */
      }
    }
  };

  /* ── state rows, visible set, comets ───────────────────────────────── */
  function rebuildState(): void {
    stateDirty = false;
    uploadNeeded = true;
    cometUpload = true;
    visible = [];
    glowing = [];
    anyGlow = false;
    cometCount = 0;
    lineRanges.length = glowRanges.length = 0;
    if (!packed) return;
    const rows = packed.paths.length;
    if (stateBuf.length !== Math.max(1, rows) * STATE_FLOATS) stateBuf = new Float32Array(Math.max(1, rows) * STATE_FLOATS);
    for (const p of packed.paths) {
      const e = entryFor(p.id);
      const a = anims.get(p.id) ?? restAnim();
      const vis = visibilityOf(p);
      const o0 = p.index * STATE_FLOATS;
      writeStateRow(stateBuf, p.index, e.s, a, p.lengthM, p.declaredLengthM, vis);
      const alpha = stateBuf[o0 + 2];
      const head = stateBuf[o0], tail = stateBuf[o0 + 1];
      if (alpha > 0.0005 && head > tail) {
        visible.push(p);
        if (stateBuf[o0 + 6] > 0.0005) glowing.push(p);
      }
      const kind = STYLE_COMET[e.s.style];
      if (kind !== "none" && a.comet > 0.002 && alpha > 0.0005) {
        const at = pointAtPacked(p, a.cometM);
        const [x, y] = relMercator(packed, at.lngLat);
        if ((cometCount + 1) * COMET_FLOATS > cometBuf.length) {
          const n = new Float32Array(cometBuf.length * 2);
          n.set(cometBuf);
          cometBuf = n;
        }
        const c = cometCount * COMET_FLOATS;
        cometBuf[c] = x;
        cometBuf[c + 1] = y;
        cometBuf[c + 2] = kind === "full" ? COMET_CORE_PX : COMET_DOT_PX;
        cometBuf[c + 3] = kind === "full" ? COMET_HALO_PX : 0;
        cometBuf[c + 4] = a.comet * alpha;
        cometBuf[c + 5] = a.cometScale;
        cometCount++;
      }
    }
    anyGlow = glowing.length > 0;
    instanceRanges(visible, lineRanges);
    instanceRanges(glowing, glowRanges);
  }

  /* ── internal animation (trail, comet) ─────────────────────────────── */
  const activeAnims = new Set<string>();
  let animOff: (() => void) | null = null;
  const tickAnims = (now: number): boolean => {
    if (destroyed) return false;
    for (const id of [...activeAnims]) {
      const a = anims.get(id);
      if (!a || !stepAnim(a, now, EASE.settle)) activeAnims.delete(id);
    }
    stateDirty = true;
    requestRender();
    if (!activeAnims.size) {
      animOff = null;
      return false;
    }
    return true;
  };
  const kickAnims = (): void => {
    if (!animOff && activeAnims.size && !destroyed) animOff = ticker.add(tickAnims);
  };

  /* ── palette drift ─────────────────────────────────────────────────── */
  const tickPalette = (now: number): boolean => {
    if (destroyed) return false;
    let busy = false;
    if (palTween) {
      const t = Math.min(1, (now - palTween.t0) / palTween.dur);
      palette = mixPalette(palTween.from, palTween.to, EASE.drift(t));
      if (t >= 1) {
        palette = palTween.to;
        palTween = null;
      } else busy = true;
    }
    if (lightTween) {
      const t = Math.min(1, (now - lightTween.t0) / lightTween.dur);
      light = mixMoodLight(lightTween.from, lightTween.to, EASE.drift(t));
      if (t >= 1) {
        light = lightTween.to;
        lightTween = null;
      } else busy = true;
    }
    requestRender();
    if (!busy) paletteOff = null;
    return busy;
  };
  const kickPalette = (): void => {
    if (!paletteOff && (palTween || lightTween) && !destroyed) paletteOff = ticker.add(tickPalette);
  };

  function paletteTo(next: GLPalette, dur = MOOD_TRANSITION_MS): void {
    if (reduced()) dur = Math.min(dur, REDUCED_MOOD_MS);
    if (dur <= 0) {
      palTween = null;
      palette = next;
      requestRender();
      return;
    }
    palTween = { from: palette, to: next, t0: performance.now(), dur };
    kickPalette();
  }

  function lightTo(next: MoodLight, dur = MOOD_TRANSITION_MS): void {
    if (reduced()) dur = Math.min(dur, REDUCED_MOOD_MS);
    if (dur <= 0) {
      lightTween = null;
      light = next;
      requestRender();
      return;
    }
    lightTween = { from: light, to: next, t0: performance.now(), dur };
    kickPalette();
  }

  function moodTo(m: Mood): void {
    const spec = MOODS[m];
    if (!spec) return;
    paletteTo(toGLPalette(spec.light));
    lightTo(moodLightFrom(spec.cssVars));
  }

  /* ── store ─────────────────────────────────────────────────────────── */
  const unsubStore = appStore.subscribe((s: AppState, prev: AppState) => {
    if (destroyed) return;
    if (s.mood !== prev.mood) moodTo(s.mood);
    if (s.layout !== prev.layout) {
      layout = s.layout;
      for (const [id, e] of entries) entries.set(id, relayout(e, layout));
      stateDirty = true;
      requestRender();
    }
    if (s.layers.routes !== prev.layers.routes || s.layers.boundary !== prev.layers.boundary) {
      layerFlags = { routes: s.layers.routes, boundary: s.layers.boundary };
      stateDirty = true;
      requestRender();
    }
    if (s.reducedMotion && !prev.reducedMotion) {
      for (const a of anims.values()) settleAnim(a);
      activeAnims.clear();
      if (palTween) palette = palTween.to;
      if (lightTween) light = lightTween.to;
      palTween = lightTween = null;
      stateDirty = true;
      requestRender();
    }
  });

  /* ── per-frame parameters ──────────────────────────────────────────── */
  function computeFrame(gl: WebGL2RenderingContext, args: CustomRenderMethodInput): FrameParams | null {
    if (!packed || !renderer) return null;
    if (stateDirty) rebuildState();
    if (!visible.length && !cometCount) return null;

    const main = args.defaultProjectionData?.mainMatrix;
    const [ox, oy] = packed.originMerc;
    if (main) {
      relativeMatrix(main, 1, ox, oy, matF32);
      relativeMatrix(main, 1, ox, oy, matF64);
    } else {
      const ws = 512 * Math.pow(2, map.getZoom());
      relativeMatrix(args.modelViewProjectionMatrix, ws, ox, oy, matF32);
      relativeMatrix(args.modelViewProjectionMatrix, ws, ox, oy, matF64);
    }
    const width = gl.drawingBufferWidth, height = gl.drawingBufferHeight;
    // the ratio MapLibre really renders at (clamped to maxCanvasSize), not map.getPixelRatio()
    const dpr = effectivePixelRatio(width, parseFloat(map.getCanvas().style.width), map.getPixelRatio());
    const near = Math.max(args.nearZ || 0, 1e-3);
    const c = map.getCenter();
    const mPerPx = (EARTH_M * Math.cos((c.lat * Math.PI) / 180)) / (512 * Math.pow(2, map.getZoom()));

    // style index order (state.ts STYLES): hero bloom preview site ring
    colors.set(palette.core, 0);
    colors.set(palette.bloom, 3);
    colors.set(palette.preview, 6);
    colors.set(palette.core, 9);
    colors.set(palette.core, 12);

    const glow = anyGlow && !gov.dropped && light.glowGain > 0;
    return {
      matrix: matF32,
      width,
      height,
      dpr,
      near,
      trailM: TRAIL_PX * mPerPx,
      shadePx: light.shade[3] > 0 ? light.shadePx : 0,
      shade: light.shade,
      glowGain: light.glowGain,
      glow,
      colors,
      trail: palette.trail,
      comet: palette.comet,
      halo: palette.halo,
      glowColor: palette.glow,
      scissor: scissorFor(width, height, dpr, near),
      comets: cometCount,
      lineRanges,
      glowRanges: glow ? glowRanges : [],
    };
  }

  /** Screen rectangle (GL origin, device px) of every lit path + comet, or null for the whole frame. */
  function scissorFor(W: number, H: number, dpr: number, near: number): [number, number, number, number] | null {
    if (!packed) return null;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    let maxW = 0;
    const [ox, oy] = packed.originMerc;
    const add = (mx: number, my: number, pad: number): boolean => {
      const cx = matF64[0] * mx + matF64[4] * my + matF64[12];
      const cy = matF64[1] * mx + matF64[5] * my + matF64[13];
      const cw = matF64[3] * mx + matF64[7] * my + matF64[15];
      if (cw < near) return false;
      const sx = (cx / cw * 0.5 + 0.5) * W, sy = (cy / cw * 0.5 + 0.5) * H;
      x0 = Math.min(x0, sx - pad);
      y0 = Math.min(y0, sy - pad);
      x1 = Math.max(x1, sx + pad);
      y1 = Math.max(y1, sy + pad);
      return true;
    };
    for (const p of visible) {
      const e = entries.get(p.id);
      maxW = Math.max(maxW, e ? e.s.width : 4);
      const [a, b, c, d] = p.bbox;
      if (!add(a - ox, b - oy, 0) || !add(c - ox, b - oy, 0) || !add(a - ox, d - oy, 0) || !add(c - ox, d - oy, 0)) return null;
    }
    const pad = (Math.max(3 * Math.max(maxW * GLOW_SIGMA_PER_WIDTH, GLOW_SIGMA_MIN_PX), (maxW + light.shadePx) / 2 + 2) + 2) * dpr;
    x0 -= pad;
    y0 -= pad;
    x1 += pad;
    y1 += pad;
    for (let i = 0; i < cometCount; i++) {
      const o = i * COMET_FLOATS;
      if (!add(cometBuf[o], cometBuf[o + 1], (COMET_HALO_PX / 2 + 2) * dpr)) return null;
    }
    x0 = Math.max(0, Math.floor(x0));
    y0 = Math.max(0, Math.floor(y0));
    x1 = Math.min(W, Math.ceil(x1));
    y1 = Math.min(H, Math.ceil(y1));
    if (x1 <= x0 || y1 <= y0) return [0, 0, 0, 0];
    if ((x1 - x0) * (y1 - y0) > 0.8 * W * H) return null;
    return [x0, y0, x1 - x0, y1 - y0];
  }

  function governor(now: number): void {
    let moving = false;
    try {
      moving = map.isMoving();
    } catch {
      /* map removed */
    }
    const was = gov.dropped;
    if (gov.sample(now, moving) && !was) {
      console.info(`[light] camera frames run at a median ${gov.median().toFixed(1)} ms over ${gov.windowSize}; glow pass dropped`);
    }
  }

  /** A GL failure inside a render hook must never escape into MapLibre's frame: switch to fallback. */
  function failGL(err: unknown): void {
    lastError = err instanceof Error ? err.message : String(err);
    try {
      renderer?.dispose();
    } catch {
      /* context gone */
    }
    renderer = null;
    frame = null;
    prepared = false;
    if (failed) return;
    failed = true;
    console.warn(`[light] WebGL light failed while rendering, switching to fallback: ${lastError}`);
    setTimeout(() => toFallback(), 0); // never remove a layer from inside a render hook
  }

  const cpu = (t0: number): void => {
    cpuSamples.push(performance.now() - t0);
    if (cpuSamples.length > 60) cpuSamples.shift();
  };

  /* ── the custom layer ──────────────────────────────────────────────── */
  const custom: CustomLayerInterface = {
    id: LAYER_IDS.light,
    type: "custom",
    renderingMode: "2d",
    onAdd(_m: MLMap, gl: WebGL2RenderingContext) {
      if (destroyed) return;
      try {
        renderer?.dispose();
        renderer = null;
        if (simulateFail) throw new Error("simulated shader failure (?light=fail)");
        renderer = new LightRenderer(gl);
        if (packed) renderer.setInstances(packed.data, packed.instanceCount);
        stateDirty = true;
        uploadNeeded = true;
      } catch (err) {
        renderer = null;
        lastError = err instanceof Error ? err.message : String(err);
        console.warn(`[light] WebGL light unavailable, switching to fallback: ${lastError}`);
        setTimeout(() => toFallback(), 0); // never remove a layer from inside onAdd
      }
    },
    onRemove() {
      renderer?.dispose();
      renderer = null;
      frame = null;
      prepared = false;
    },
    prerender(gl: WebGL2RenderingContext, args: CustomRenderMethodInput) {
      if (!renderer || destroyed) return;
      const t0 = performance.now();
      try {
        frame = computeFrame(gl, args);
        prepared = !!frame;
        if (frame) {
          renderer.prerender(frame, uploadNeeded ? stateBuf : null, packed ? packed.paths.length : 0, cometUpload ? cometBuf : null);
          uploadNeeded = false;
          cometUpload = false;
        }
      } catch (err) {
        failGL(err);
      }
      cpu(t0);
    },
    render(gl: WebGL2RenderingContext, args: CustomRenderMethodInput) {
      if (!renderer || destroyed) return;
      const t0 = performance.now();
      governor(t0);
      try {
        if (!prepared) {
          // prerender did not run this frame: do the offscreen passes here (restores the bound framebuffer)
          frame = computeFrame(gl, args);
          if (frame) {
            renderer.prerender(frame, uploadNeeded ? stateBuf : null, packed ? packed.paths.length : 0, cometUpload ? cometBuf : null);
            uploadNeeded = false;
            cometUpload = false;
          }
        }
        if (frame && renderer) renderer.render(frame);
      } catch (err) {
        failGL(err);
      }
      prepared = false;
      cpu(t0);
    },
  };

  function addCustom(): void {
    if (destroyed || mode !== "gl") return;
    try {
      if (map.getLayer(LAYER_IDS.light)) return;
      const before = o.beforeId && map.getLayer(o.beforeId) ? o.beforeId : undefined;
      map.addLayer(custom, before);
    } catch {
      /* style not loaded yet: the next style event retries */
    }
  }

  function removeCustom(): void {
    try {
      if (map.getLayer(LAYER_IDS.light)) map.removeLayer(LAYER_IDS.light);
    } catch {
      /* style gone */
    }
  }

  function toFallback(): void {
    if (destroyed || mode === "fallback") return;
    mode = "fallback";
    removeCustom();
    renderer = null;
    startFallback();
    for (const cb of [...modeListeners]) cb("fallback");
  }

  function startFallback(): void {
    if (!fallback) fallback = new LightFallback(map, o.beforeId);
    fallback.attach();
    if (packed) fallback.setPaths(packed.paths);
    scheduleFallback();
  }

  /* ── fallback frame (ticker-driven, one flush per frame) ───────────── */
  let fallbackQueued = false;
  function scheduleFallback(): void {
    if (fallbackQueued || destroyed) return;
    fallbackQueued = true;
    ticker.add(() => {
      fallbackQueued = false;
      flushFallback();
      return false;
    });
  }

  function flushFallback(): void {
    if (!fallback || destroyed || mode !== "fallback") return;
    if (stateDirty) rebuildState();
    const f: FallbackFrame = { hero: null, rest: [], palette, light, comet: null };
    if (packed) {
      let best: { p: PackedPath; e: StateEntry; score: number } | null = null;
      for (const p of packed.paths) {
        const e = entries.get(p.id);
        if (!e) continue;
        const vis = visibilityOf(p);
        const alpha = e.s.alpha * vis;
        const head = isArrived(e.s, p.declaredLengthM) ? p.lengthM : toDrawnM(p, e.s.head);
        const tail = toDrawnM(p, e.s.tail);
        if (!(alpha > 0.0005 && head > tail)) continue;
        if (e.s.style === "hero") {
          const score = alpha * 1e6 + (touched.get(p.id) ?? 0);
          if (!best || score > best.score) best = { p, e, score };
        }
      }
      for (const p of packed.paths) {
        const e = entries.get(p.id);
        if (!e) continue;
        const vis = visibilityOf(p);
        const a = anims.get(p.id) ?? restAnim();
        const head = isArrived(e.s, p.declaredLengthM) ? p.lengthM : toDrawnM(p, e.s.head);
        const tail = toDrawnM(p, e.s.tail);
        const alpha = e.s.alpha * vis;
        if (best && best.p === p) {
          f.hero = { path: p, headM: head, tailM: tail, alpha, width: e.s.width, glow: e.s.glow, trail: a.trail, style: e.s.style };
          if (a.comet > 0.002 && STYLE_COMET[e.s.style] !== "none") {
            f.comet = { lngLat: pointAtPacked(p, a.cometM).lngLat as LngLat, alpha: a.comet * alpha, scale: a.cometScale };
          }
          continue;
        }
        if (!(alpha > 0.0005 && head > tail)) continue;
        const frac = (head - tail) / p.lengthM;
        const ease = Math.min(1, frac / 0.35);
        f.rest.push({ path: p, alpha: alpha * ease * ease * (3 - 2 * ease), width: e.s.width, style: e.s.style });
      }
    }
    try {
      fallback.update(f);
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
    }
  }

  /* ── map events ────────────────────────────────────────────────────── */
  const onStyle = (): void => {
    if (destroyed) return;
    if (mode === "gl") addCustom();
    else if (fallback && fallback.attach() > 0) scheduleFallback(); // styledata also fires after any paint change
  };
  const onLost = (): void => {
    renderer = null; // its objects died with the context; onRemove may still dispose them harmlessly
    frame = null;
    prepared = false;
  };
  const onRestored = (): void => {
    stateDirty = true;
    uploadNeeded = true;
    onStyle();
  };
  map.on("style.load", onStyle);
  map.on("styledata", onStyle);
  map.on("webglcontextlost", onLost);
  map.on("webglcontextrestored", onRestored);

  if (mode === "gl") addCustom();
  else startFallback();

  /* ── API ───────────────────────────────────────────────────────────── */
  const api: LightLayerHandle = {
    id: LAYER_IDS.light,
    get mode() {
      return mode;
    },

    setPaths(paths: LightPath[]): void {
      if (destroyed) return;
      const app = appStore.getState();
      const first = paths.find((p) => p.coords && p.coords.length)?.coords[0];
      const origin: LngLat = (app.routes?.origin ?? app.data?.origin.coord ?? first ?? [0, 0]) as LngLat;
      packed = packPaths(paths ?? [], origin);
      kinds.clear();
      for (const p of packed.paths) kinds.set(p.id, p.kind);
      const keep = new Set(packed.paths.map((p) => p.id));
      for (const id of [...entries.keys()]) if (!keep.has(id)) entries.delete(id);
      for (const id of [...anims.keys()]) if (!keep.has(id)) anims.delete(id);
      for (const id of [...styled]) if (!keep.has(id)) styled.delete(id);
      for (const id of [...touched.keys()]) if (!keep.has(id)) touched.delete(id);
      for (const p of packed.paths) {
        const e = entries.get(p.id);
        if (!e) entryFor(p.id);
        else if (!styled.has(p.id) && e.s.style !== styleForKind(p.kind)) {
          // state set before the path's kind was known: take the kind's style defaults
          entries.set(p.id, applyPatch(e, { style: styleForKind(p.kind) }, layout));
        }
      }
      renderer?.setInstances(packed.data, packed.instanceCount);
      fallback?.setPaths(packed.paths);
      stateDirty = true;
      uploadNeeded = true;
      requestRender();
    },

    setState(id: string, s: Partial<RouteVisualState>): void {
      if (destroyed || !s) return;
      const e = entryFor(id);
      const prev = e.s;
      if (s.style !== undefined) styled.add(id);
      const next = applyPatch(e, s, layout);
      entries.set(id, next);
      touched.set(id, ++touchSeq);
      const p = packed?.byId.get(id);
      const declared = p ? p.declaredLengthM : Math.max(next.s.head, 1);
      let a = anims.get(id);
      if (!a) {
        a = restAnim();
        anims.set(id, a);
      }
      onStateChange(a, prev, next.s, declared, performance.now(), reduced());
      if (a.trailTw || a.cometTw) {
        activeAnims.add(id);
        kickAnims();
      }
      stateDirty = true;
      requestRender();
    },

    getState(id: string): RouteVisualState | undefined {
      const e = entries.get(id);
      return e ? copyState(e.s) : undefined;
    },

    setPalette(p: LightPalette): void {
      if (destroyed || !p) return;
      paletteTo(toGLPalette(p));
    },

    pointAt(id: string, m: number): { lngLat: LngLat; bearing: number } | null {
      const p = packed?.byId.get(id);
      if (!p || !Number.isFinite(m)) return null;
      return pointAtPacked(p, m);
    },

    requestRender,

    onModeChange(cb: (mode: "gl" | "fallback") => void): () => void {
      modeListeners.add(cb);
      return () => modeListeners.delete(cb);
    },

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      unsubStore();
      animOff?.();
      paletteOff?.();
      animOff = paletteOff = null;
      map.off("style.load", onStyle);
      map.off("styledata", onStyle);
      map.off("webglcontextlost", onLost);
      map.off("webglcontextrestored", onRestored);
      removeCustom();
      renderer?.dispose();
      renderer = null;
      fallback?.destroy();
      fallback = null;
      modeListeners.clear();
    },

    debug(): LightDebugInfo {
      const avg = (a: number[]) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
      return {
        mode,
        paths: packed?.paths.length ?? 0,
        instances: packed?.instanceCount ?? 0,
        visible: visible.length,
        comets: cometCount,
        glow: anyGlow && !gov.dropped && light.glowGain > 0,
        glowDropped: gov.dropped,
        frameMsAvg: Math.round(gov.frameMsAvg() * 100) / 100,
        cpuMsAvg: Math.round(avg(cpuSamples) * 1000) / 1000,
        lastError,
      };
    },
  };
  return api;
}

/** Compile-time check: the implementation satisfies the contract's factory type. */
const _factory: CreateLightLayer = createLightLayer;
void _factory;
